/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect } from "react";
import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import CustomDataGrid from "../../../components/common/DataGrid/CustomDataGrid";
import AdminStatCard from "../../../components/admin/stat-card/AdminStatCard";
import OrdersAPI from "../../../api/firebase/OrdersAPI";
import ViewOrder from "../../../components/admin/view-order/ViewOrder";
import { formatDateTimeForAdmin } from "../../../utils/dateUtils";
import { styles } from "./styles";

// Enhanced Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CurrencyRupee from "@mui/icons-material/CurrencyRupee";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import TuneIcon from "@mui/icons-material/Tune";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const currencyFormat = (num) => {
  if (num === null || num === undefined) return "₹0";
  const parsed = Number(num);
  if (isNaN(parsed)) return "₹0";
  return "₹" + parsed.toLocaleString("en-IN");
};

const getStatusBadgeConfig = (statusRaw = "") => {
  const status = statusRaw.toLowerCase();
  if (status.includes("delivered")) {
    return {
      label: "Delivered",
      bgcolor: "#ecfdf5",
      color: "#047857",
      borderColor: "#a7f3d0",
      dotColor: "#10b981",
    };
  }
  if (status.includes("shipped")) {
    return {
      label: "Shipped",
      bgcolor: "#f5f3ff",
      color: "#6d28d9",
      borderColor: "#ddd6fe",
      dotColor: "#8b5cf6",
    };
  }
  if (status.includes("out for delivery")) {
    return {
      label: "Out for Delivery",
      bgcolor: "#ecfeff",
      color: "#0e7490",
      borderColor: "#a5f3fc",
      dotColor: "#06b6d4",
    };
  }
  if (status.includes("placed") || status.includes("pending")) {
    return {
      label: statusRaw || "Placed",
      bgcolor: "#fffbeb",
      color: "#b45309",
      borderColor: "#fde68a",
      dotColor: "#f59e0b",
    };
  }
  if (status.includes("cancel")) {
    return {
      label: statusRaw || "Cancelled",
      bgcolor: "#fef2f2",
      color: "#b91c1c",
      borderColor: "#fecaca",
      dotColor: "#ef4444",
    };
  }
  if (status.includes("refund")) {
    return {
      label: "Refunded",
      bgcolor: "#fff7ed",
      color: "#c2410c",
      borderColor: "#fed7aa",
      dotColor: "#f97316",
    };
  }
  return {
    label: statusRaw || "Pending",
    bgcolor: "#fffbeb",
    color: "#b45309",
    borderColor: "#fde68a",
    dotColor: "#f59e0b",
  };
};

const getPaymentBadgeConfig = (payment) => {
  const method = (payment?.method || "").toUpperCase();
  const isCod = method.includes("COD") || method.includes("CASH");
  if (isCod) {
    return {
      label: "COD",
      bgcolor: "#fffbeb",
      color: "#b45309",
      borderColor: "#fde68a",
    };
  }
  return {
    label: method || "ONLINE",
    bgcolor: "#f0fdf4",
    color: "#166534",
    borderColor: "#bbf7d0",
  };
};

const AllOrders = () => {
  const [ordersArr, setOrdersArr] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [openViewOrderModal, setOpenViewOrderModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [paymentFilter, setPaymentFilter] = useState("ALL");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const ordersAPI = useMemo(() => new OrdersAPI(), []);

  // Snackbar notifications
  const [snackbarState, setSnackbarState] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" });

  const showSnackbar = (text, type = "success") => {
    setMessage({ text, type });
    setSnackbarState(true);
  };

  const hideSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState(false);
  };

  useEffect(() => {
    if (selectedOrder.id !== undefined) {
      setOpenViewOrderModal(true);
    }
  }, [selectedOrder]);

  // Fetch all orders
  const fetchData = async () => {
    setIsRefreshing(true);
    try {
      const resData = await ordersAPI.getOrders();
      if (resData && resData.data) {
        setOrdersArr(resData.data);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      showSnackbar("Failed to refresh orders data.", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const visibleOrders = useMemo(
    () => ordersArr.filter((order) => String(order.status || "").trim().toUpperCase() !== "CREATED"),
    [ordersArr]
  );

  useEffect(() => {
    if (message.type === "success") {
      const timer = setTimeout(() => {
        setOpenViewOrderModal(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // KPI Calculations
  const totalRevenue = useMemo(() => {
    return visibleOrders.reduce((sum, order) => {
      const status = (order.status || "").toLowerCase();
      if (status === "created" || status.includes("cancelled")) return sum;
      const amt = Number(order.amount) || 0;
      return sum + amt;
    }, 0);
  }, [visibleOrders]);

  const placedCount = useMemo(() => {
    return visibleOrders.filter((o) => (o.status || "").toLowerCase().includes("placed")).length;
  }, [visibleOrders]);

  const shippedCount = useMemo(() => {
    return visibleOrders.filter((o) => {
      const s = (o.status || "").toLowerCase();
      return s.includes("shipped") || s.includes("out for delivery");
    }).length;
  }, [visibleOrders]);

  const deliveredCount = useMemo(() => {
    return visibleOrders.filter((o) => (o.status || "").toLowerCase().includes("delivered")).length;
  }, [visibleOrders]);

  const cancelledCount = useMemo(() => {
    return visibleOrders.filter((o) => (o.status || "").toLowerCase().includes("cancelled")).length;
  }, [visibleOrders]);

  // Filtered Orders
  const filteredOrders = useMemo(() => {
    return visibleOrders.filter((order) => {
      // 1. Text Search
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const itemTitles = (order.items || []).map((i) => i.title).join(" ");
        const matches = [
          order.id,
          order.user?.name,
          order.user?.email,
          order.user?.mobile,
          order.address?.name,
          order.address?.city,
          order.address?.pincode,
          order.address?.state,
          order.courierDetails?.partner,
          order.courierDetails?.trackingId,
          order.payment?.method,
          order.status,
          itemTitles,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

        if (!matches) return false;
      }

      // 2. Status Filter
      if (statusFilter !== "ALL") {
        const orderStatus = (order.status || "").toLowerCase();
        const targetStatus = statusFilter.toLowerCase();
        if (targetStatus === "cancelled") {
          if (!orderStatus.includes("cancelled")) return false;
        } else if (targetStatus === "placed") {
          if (!orderStatus.includes("placed")) return false;
        } else if (targetStatus === "shipped") {
          if (!orderStatus.includes("shipped") && !orderStatus.includes("out for delivery")) {
            return false;
          }
        } else if (targetStatus === "delivered") {
          if (!orderStatus.includes("delivered")) return false;
        } else if (orderStatus !== targetStatus) {
          return false;
        }
      }

      // 3. Payment Filter
      if (paymentFilter !== "ALL") {
        const method = (order.payment?.method || "").toUpperCase();
        const isCod = method.includes("COD") || method.includes("CASH");
        if (paymentFilter === "COD" && !isCod) return false;
        if (paymentFilter === "ONLINE" && isCod) return false;
      }

      return true;
    });
  }, [visibleOrders, searchQuery, statusFilter, paymentFilter]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setStatusFilter("ALL");
    setPaymentFilter("ALL");
  };

  const hasActiveFilters =
    searchQuery.trim() !== "" || statusFilter !== "ALL" || paymentFilter !== "ALL";

  const activeFiltersCount =
    (searchQuery.trim() ? 1 : 0) +
    (statusFilter !== "ALL" ? 1 : 0) +
    (paymentFilter !== "ALL" ? 1 : 0);

  // Columns for Orders DataGrid
  const columns = useMemo(
    () => [
      {
        field: "image",
        headerName: "Items",
        width: 85,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const items = params.row.items || [];
          return (
            <AvatarGroup
              max={2}
              variant="rounded"
              sx={{
                "& .MuiAvatar-root": {
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  bgcolor: "#f8fafc",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                },
              }}
            >
              {items.map((item, idx) => {
                const imgUrl = item.images?.[0]?.url;
                return (
                  <Avatar
                    key={item.id || idx}
                    alt={item.title || "Product"}
                    src={imgUrl || ""}
                  >
                    <ShoppingBagOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </Avatar>
                );
              })}
            </AvatarGroup>
          );
        },
      },
      {
        field: "id",
        headerName: "Order ID",
        hide: true,
      },
      {
        field: "customer",
        headerName: "Customer",
        width: 180,
        valueGetter: (params) => params.row.user?.name || params.row.address?.name || "Customer",
        renderCell: (params) => {
          const name = params.row.user?.name || params.row.address?.name || "Guest Customer";
          const location =
            params.row.address?.city ||
            params.row.user?.mobile ||
            params.row.address?.mobile ||
            params.row.user?.email ||
            "";
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2, overflow: "hidden" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {name}
              </Typography>
              {location && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.72rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {location}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "items",
        headerName: "Products Ordered",
        flex: 1,
        minWidth: 240,
        renderCell: (params) => {
          const items = params.row.items || [];
          if (items.length === 0) {
            return (
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                No items
              </Typography>
            );
          }
          const firstItem = items[0];
          const remainingCount = items.length - 1;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, overflow: "hidden" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#334155",
                  fontSize: "0.82rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {firstItem.title}
                <Typography
                  component="span"
                  sx={{ color: "#64748b", fontWeight: 700, ml: 0.5, fontSize: "0.75rem" }}
                >
                  ×{firstItem.quantity}
                </Typography>
              </Typography>
              {remainingCount > 0 && (
                <Chip
                  label={`+${remainingCount}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    bgcolor: "#f1f5f9",
                    color: "#475569",
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: "amount",
        headerName: "Total Amount",
        width: 140,
        type: "number",
        align: "left",
        headerAlign: "left",
        valueGetter: (params) => Number(params.row.amount) || 0,
        renderCell: (params) => {
          return (
            <Typography variant="body2" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "0.9rem" }}>
              {currencyFormat(params.row.amount)}
            </Typography>
          );
        },
      },
      {
        field: "payment",
        headerName: "Payment",
        width: 130,
        renderCell: (params) => {
          const config = getPaymentBadgeConfig(params.row.payment);
          return (
            <Chip
              label={config.label}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.72rem",
                fontWeight: 800,
                letterSpacing: "0.02em",
                bgcolor: config.bgcolor,
                color: config.color,
                border: `1px solid ${config.borderColor}`,
              }}
            />
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        renderCell: (params) => {
          const config = getStatusBadgeConfig(params.row.status);
          return (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.8,
                px: 1.2,
                py: 0.4,
                borderRadius: "20px",
                bgcolor: config.bgcolor,
                color: config.color,
                border: `1px solid ${config.borderColor}`,
                fontSize: "0.75rem",
                fontWeight: 700,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: config.dotColor,
                }}
              />
              <span>{config.label}</span>
            </Box>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Date",
        width: 140,
        valueGetter: (params) => {
          const createdAt = params.row.createdAt;
          if (typeof createdAt === "string") {
            const date = new Date(createdAt);
            return isNaN(date.getTime()) ? new Date(0) : date;
          } else if (createdAt && createdAt.seconds) {
            return new Date(createdAt.seconds * 1000);
          } else if (createdAt && createdAt.toDate) {
            return createdAt.toDate();
          }
          return new Date(0);
        },
        renderCell: (params) => {
          return (
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
              {formatDateTimeForAdmin(params.row.createdAt)}
            </Typography>
          );
        },
      },
    ],
    []
  );

  // Email notifications logic
  const sendOrderShippedMail = async (data) => {
    const shippingAmount = data.shippingCost || 0;
    const url = "https://payments.inproveda.com/public/api/order_shipped";
    const customerName = data.user?.name || data.address?.name || "Customer";
    const customerEmail = data.user?.email || "";
    const orderId = data.id;

    const tableHeader =
      "<table style='width: 100%;'>" +
      "<tr>" +
      "<th style='text-align:left'></th>" +
      "<th style='text-align:left'>Product</th>" +
      "<th style='text-align:left'>List Price</th>" +
      "<th style='text-align:left'>Total</th>" +
      "</tr>";

    let tableBody = "";
    (data.items || []).forEach((item, index) => {
      tableBody +=
        "<tr><td style='vertical-align: top;'>" +
        (index + 1) +
        ".</td><td style='padding-right:16px'>" +
        item.title +
        "  x " +
        item.quantity +
        " </td> <td>₹" +
        item.listPrice +
        "</td><td>₹" +
        item.listPrice * item.quantity +
        "</td></tr>";
    });

    const totalItemsAmount = (data.items || []).reduce(
      (accumulator, currentValue) =>
        accumulator + currentValue.listPrice * currentValue.quantity,
      0
    );

    const tableShippingAmount =
      "<tr><td></td><td></td><td>Shipping</td><td>" +
      (shippingAmount === 0 ? "Free" : `₹${shippingAmount}`) +
      "</td></tr>";

    let tableCouponDiscount = "";
    let couponDiscount = 0;
    const couponApplied = data.couponApplied;
    if (couponApplied !== null && couponApplied !== undefined) {
      couponDiscount = couponApplied.amount || 0;
      tableCouponDiscount =
        `<tr><td></td><td></td><td>Coupon (${couponApplied.couponCode})</td><td>- ₹` +
        couponDiscount +
        "</td></tr>";
    }

    const tableFinalAmount =
      "<tr><td></td><td></td><td>Final Amount</td><td>₹" +
      (totalItemsAmount + shippingAmount - couponDiscount) +
      "</td></tr>";

    const orderSummaryTable =
      tableHeader +
      tableBody +
      tableShippingAmount +
      tableCouponDiscount +
      tableFinalAmount +
      "</table>";

    const selAddress = data.address || {};
    const nameTrimmed = (selAddress.name || "").replace(/[^a-zA-Z0-9,()-+ ]/g, "");
    const addressTrimmed = (selAddress.address || "").replace(/[^a-zA-Z0-9,()-+ ]/g, "");
    const cityTrimmed = (selAddress.city || "").replace(/[^a-zA-Z0-9,()-+ ]/g, "");
    const pincodeTrimmed = (selAddress.pincode || "").replace(/[^a-zA-Z0-9,()-+ ]/g, "");
    const landmarkTrimmed = (selAddress.landmark || "").replace(/[^a-zA-Z0-9,() ]/g, "");

    const shippingAddressData =
      "<div><p>" +
      nameTrimmed +
      "</p>" +
      "<p>" +
      (selAddress.mobile || "") +
      "," +
      (selAddress.alternateMobile || "") +
      "</p><p>Address: " +
      addressTrimmed +
      "</p><p>City: " +
      cityTrimmed +
      "</p><p>State: " +
      (selAddress.state || "") +
      "</p><p>Pincode: " +
      pincodeTrimmed +
      "</p><p>Landmark: " +
      landmarkTrimmed +
      "</p></div>";

    const shipmentProvider = data.courierDetails?.partner || "";
    const trackingURL = data.courierDetails?.trackingUrl || "";
    const trackingID = data.courierDetails?.trackingId || "";

    const options = JSON.stringify({
      cust_name: customerName,
      cust_email: customerEmail,
      from_name: "Inproveda Orders",
      from_email: "orders@inproveda.com",
      bcc_name: "Arpit Goyal",
      bcc_email: "arpitgoyal138@gmail.com",
      order_id: orderId,
      order_summary: orderSummaryTable,
      shipping_address: shippingAddressData,
      shipment_provider: shipmentProvider,
      tracking_url: trackingURL,
      tracking_id: trackingID,
    });

    try {
      await fetch(url, {
        method: "POST",
        body: options,
        headers: {
          "content-type": "application/json",
        },
      });
    } catch (error) {
      console.error("Server Error sending shipped email:", error);
    }
  };

  const sendOrderCancelledOrDeliveredMail = async (data) => {
    let url = "";
    if (data.status === "Delivered") {
      url = "https://payments.inproveda.com/public/api/order_delivered";
    } else {
      url = "https://payments.inproveda.com/public/api/order_cancelled";
    }
    const shippingAmount = 0;
    const customerName = data.user?.name || data.address?.name || "Customer";
    const customerEmail = data.user?.email || "";
    const orderId = data.id;

    const tableHeader =
      "<table style='width: 100%;'>" +
      "<tr>" +
      "<th style='text-align:left'></th>" +
      "<th style='text-align:left'>Product</th>" +
      "<th style='text-align:left'>List Price</th>" +
      "<th style='text-align:left'>Total</th>" +
      "</tr>";

    let tableBody = "";
    (data.items || []).forEach((item, index) => {
      tableBody +=
        "<tr><td style='vertical-align: top;'>" +
        (index + 1) +
        ".</td><td style='padding-right:16px'>" +
        item.title +
        "  x " +
        item.quantity +
        " </td> <td>₹" +
        item.listPrice +
        "</td><td>₹" +
        item.listPrice * item.quantity +
        "</td></tr>";
    });

    const totalAmount = (data.items || []).reduce(
      (accumulator, currentValue) =>
        accumulator + currentValue.listPrice * currentValue.quantity,
      0
    );

    let tableCouponDiscount = "";
    let couponDiscount = 0;
    if (data.couponApplied) {
      tableCouponDiscount =
        `<tr><td></td><td></td><td>Coupon (${data.couponApplied.couponCode})</td><td>- ₹` +
        data.couponApplied.amount +
        "</td></tr>";
      couponDiscount = data.couponApplied.amount || 0;
    }

    const tableFinalAmount =
      "<tr><td></td><td></td><td>Shipping</td><td>₹" +
      shippingAmount +
      "</td></tr>" +
      tableCouponDiscount +
      "<tr><td></td><td></td><td>Final Amount</td><td>₹" +
      (totalAmount + shippingAmount - couponDiscount) +
      "</td></tr>";

    const orderSummaryTable =
      tableHeader + tableBody + tableFinalAmount + "</table>";

    const options = JSON.stringify({
      cust_name: customerName,
      cust_email: customerEmail,
      from_name: "Inproveda Orders",
      from_email: "orders@inproveda.com",
      bcc_name: "Arpit Goyal",
      bcc_email: "arpitgoyal138@gmail.com",
      order_id: orderId,
      order_summary: orderSummaryTable,
    });

    try {
      await fetch(url, {
        method: "POST",
        body: options,
        headers: {
          "content-type": "application/json",
        },
      });
    } catch (error) {
      console.error("Server Error sending email:", error);
    }
  };

  // Update order status
  const handleSetOrder = async (dataToUpdate) => {
    const dataToSend = {
      id: dataToUpdate.id,
      payload: {
        ...dataToUpdate,
      },
    };

    try {
      const resData = await ordersAPI.setOrder(dataToSend);
      if (resData.success) {
        if (dataToUpdate.status === "Shipped") {
          sendOrderShippedMail(dataToUpdate);
        } else if (
          dataToUpdate.status === "Cancelled by buyer" ||
          dataToUpdate.status === "Cancelled by seller" ||
          dataToUpdate.status === "Delivered"
        ) {
          sendOrderCancelledOrDeliveredMail(dataToUpdate);
        }
        const orderIndexInArr = ordersArr.findIndex((obj) => obj.id === dataToUpdate.id);
        const orders_ = [...ordersArr];
        if (orderIndexInArr !== -1) {
          orders_[orderIndexInArr] = {
            ...dataToUpdate,
          };
          setOrdersArr(orders_);
        }

        showSnackbar("Order updated successfully!", "success");
      } else {
        showSnackbar(resData.message || "Some error occurred. Please try again!", "error");
      }
    } catch (ex) {
      showSnackbar("Some error occurred. Please try again!", "error");
      console.error(ex);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    const result = await ordersAPI.deleteOrder(orderId);
    if (!result?.success) {
      showSnackbar(result?.message || "Failed to delete draft order.", "error");
      throw new Error(result?.message || "Failed to delete draft order");
    }

    setOrdersArr((currentOrders) => currentOrders.filter((order) => order.id !== orderId));
    setSelectedOrder({});
    setOpenViewOrderModal(false);
    showSnackbar("Draft order deleted successfully!", "success");
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Hero Header matching Products */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 4,
          color: "#ffffff",
          background: "linear-gradient(135deg, #00382e 0%, #004d40 60%, #056152 100%)",
          boxShadow: "0 10px 25px -5px rgba(0, 77, 64, 0.3)",
          display: { xs: "none", sm: "flex" },
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.75rem", sm: "2.2rem" },
              letterSpacing: "-0.02em",
            }}
          >
            Orders
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.5, maxWidth: 560 }}>
            Monitor fulfillment, manage courier dispatches, review payment states, and track customer deliveries in real time.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ zIndex: 1, alignSelf: { xs: "stretch", sm: "auto" } }}
        >
          <Tooltip title="Refresh Orders Data">
            <IconButton
              onClick={fetchData}
              disabled={isRefreshing}
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#ffffff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: isRefreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>

          <Chip
            icon={<CurrencyRupee sx={{ color: "#004d40 !important", fontSize: 16 }} />}
            label={`Net Revenue: ${currencyFormat(totalRevenue)}`}
            sx={{
              fontWeight: 800,
              bgcolor: "#ffffff",
              color: "#004d40",
              px: 1.5,
              py: 2.2,
              borderRadius: "10px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              fontSize: "0.875rem",
            }}
          />
        </Stack>
      </Box>

      {/* KPI Summary Cards */}
      <Grid2 container spacing={{ xs: 1.25, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        {/* Total Orders Card */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Total Orders"
            value={visibleOrders.length}
            note={`${deliveredCount} delivered • ${placedCount + shippedCount} in-flight • ${cancelledCount} cancelled`}
            icon={<ShoppingBagOutlinedIcon />}
            onClick={handleResetFilters}
          />
        </Grid2>

        {/* Placed & Pending */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Placed & Pending"
            value={placedCount}
            note="Awaiting packaging & courier pickup"
            icon={<WarningAmberRoundedIcon />}
            color="#b45309"
            background="#fffbeb"
            selected={statusFilter === "Placed"}
            onClick={() => setStatusFilter(statusFilter === "Placed" ? "ALL" : "Placed")}
            delay={70}
          />
        </Grid2>

        {/* Shipped & In Transit */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Shipped & Transit"
            value={shippedCount}
            note="Dispatched with courier partner"
            icon={<LocalShippingOutlinedIcon />}
            color="#6d28d9"
            background="#f5f3ff"
            selected={statusFilter === "Shipped"}
            onClick={() => setStatusFilter(statusFilter === "Shipped" ? "ALL" : "Shipped")}
            delay={140}
          />
        </Grid2>

        {/* Delivered & Completed */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Delivered"
            value={deliveredCount}
            note="Successfully received by customer"
            icon={<CheckCircleOutlineRoundedIcon />}
            color="#047857"
            background="#ecfdf5"
            selected={statusFilter === "Delivered"}
            onClick={() => setStatusFilter(statusFilter === "Delivered" ? "ALL" : "Delivered")}
            delay={210}
          />
        </Grid2>
      </Grid2>

      {/* Filter and Search Bar */}
      {!isMobile && (
        <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3, mb: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search by order ID, customer name, email, phone, city, or product..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: 2 },
              }}
            />

            {/* Quick Filter Dropdowns */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              {/* Status Filter */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="order-status-filter-label">Order Status</InputLabel>
                <Select
                  labelId="order-status-filter-label"
                  value={statusFilter}
                  label="Order Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
                >
                  <MenuItem value="ALL">All Statuses</MenuItem>
                  <MenuItem value="Placed">Placed</MenuItem>
                  <MenuItem value="Shipped">Shipped</MenuItem>
                  <MenuItem value="Out for delivery">Out for Delivery</MenuItem>
                  <MenuItem value="Delivered">Delivered</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                  <MenuItem value="Refunded">Refunded</MenuItem>
                </Select>
              </FormControl>

              {/* Payment Filter */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="order-payment-filter-label">Payment Method</InputLabel>
                <Select
                  labelId="order-payment-filter-label"
                  value={paymentFilter}
                  label="Payment Method"
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
                >
                  <MenuItem value="ALL">All Payments</MenuItem>
                  <MenuItem value="ONLINE">Online / Prepaid</MenuItem>
                  <MenuItem value="COD">Cash on Delivery (COD)</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {/* Active Filter Pills Bar */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
                Active Filters:
              </Typography>

              {searchQuery && (
                <Chip
                  size="small"
                  label={`Search: "${searchQuery}"`}
                  onDelete={() => setSearchQuery("")}
                  sx={{ borderRadius: 1.5 }}
                />
              )}

              {statusFilter !== "ALL" && (
                <Chip
                  size="small"
                  label={`Status: ${statusFilter}`}
                  onDelete={() => setStatusFilter("ALL")}
                  sx={{ borderRadius: 1.5 }}
                />
              )}

              {paymentFilter !== "ALL" && (
                <Chip
                  size="small"
                  label={`Payment: ${paymentFilter}`}
                  onDelete={() => setPaymentFilter("ALL")}
                  sx={{ borderRadius: 1.5 }}
                />
              )}

              <Chip
                size="small"
                label="Clear all"
                onClick={handleResetFilters}
                color="error"
                variant="outlined"
                sx={{ borderRadius: 1.5, fontWeight: 700 }}
              />

              <Typography variant="caption" sx={{ color: "#94a3b8", ml: "auto !important" }}>
                Showing {filteredOrders.length} of {visibleOrders.length} orders
              </Typography>
            </Stack>
          )}
        </Stack>
        </Paper>
      )}

      {isMobile && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", mb: 1.25 }}>
            Orders ({filteredOrders.length})
          </Typography>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={
              <Badge badgeContent={activeFiltersCount} color="error">
                <TuneIcon fontSize="small" />
              </Badge>
            }
            onClick={() => setMobileFilterOpen(true)}
            sx={{
              minHeight: 40,
              borderColor: "#94a3b8",
              color: "#334155",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.78rem",
              "&:hover": { borderColor: "#004d40", bgcolor: "#f0fdfa" },
            }}
          >
            Filter & Search
          </Button>

          {hasActiveFilters && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
              {searchQuery.trim() && (
                <Chip size="small" label={`"${searchQuery}"`} onDelete={() => setSearchQuery("")} />
              )}
              {statusFilter !== "ALL" && (
                <Chip size="small" label={statusFilter} onDelete={() => setStatusFilter("ALL")} />
              )}
              {paymentFilter !== "ALL" && (
                <Chip size="small" label={paymentFilter} onDelete={() => setPaymentFilter("ALL")} />
              )}
              <Button
                size="small"
                onClick={handleResetFilters}
                sx={{ minWidth: 0, p: 0.5, fontSize: "0.72rem", fontWeight: 700, color: "#dc2626" }}
              >
                Clear
              </Button>
            </Stack>
          )}

          <Drawer
            anchor="bottom"
            open={mobileFilterOpen}
            onClose={() => setMobileFilterOpen(false)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: "24px",
                borderTopRightRadius: "24px",
                maxHeight: "88vh",
                p: 2.5,
                pb: "max(28px, env(safe-area-inset-bottom))",
                bgcolor: "#f8fafc",
                border: "1px solid #dbe7e4",
                borderBottom: 0,
                background: "linear-gradient(180deg, #ecfdf8 0%, #ffffff 24%, #f8fafc 60%)",
                boxShadow: "0 -14px 42px rgba(15, 23, 42, 0.18)",
              },
            }}
            ModalProps={{ keepMounted: true, hideBackdrop: true }}
          >
            <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: "#cbd5e1", mx: "auto", mb: 2 }} />

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    Filter & Search
                  </Typography>
                  {activeFiltersCount > 0 && (
                    <Chip
                      size="small"
                      label={activeFiltersCount}
                      aria-label={`${activeFiltersCount} active filters`}
                      sx={{ height: 22, minWidth: 24, bgcolor: "#e6f4f1", color: "#004d40", fontWeight: 800 }}
                    />
                  )}
                </Stack>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  {filteredOrders.length} of {visibleOrders.length} orders
                </Typography>
              </Box>
              <Tooltip title="Close filters">
                <IconButton
                  size="small"
                  aria-label="Close filters"
                  onClick={() => setMobileFilterOpen(false)}
                  sx={{ bgcolor: "#ffffff", color: "#475569" }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#334155", mb: 1 }}>
                Search
              </Typography>
              <TextField
                fullWidth
                placeholder="Order ID, customer, phone, city, or product"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: "#64748b" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => setSearchQuery("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ "& .MuiOutlinedInput-root": { height: 48, bgcolor: "#ffffff", borderRadius: 2 } }}
              />
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#334155", mb: 1 }}>
                Order status
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ height: 48, borderRadius: 2, bgcolor: "#ffffff", fontWeight: 600 }}
                >
                  <MenuItem value="ALL">All statuses</MenuItem>
                  <MenuItem value="Placed">Placed</MenuItem>
                  <MenuItem value="Shipped">Shipped</MenuItem>
                  <MenuItem value="Out for delivery">Out for Delivery</MenuItem>
                  <MenuItem value="Delivered">Delivered</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                  <MenuItem value="Refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#334155", mb: 1 }}>
                Payment method
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  sx={{ height: 48, borderRadius: 2, bgcolor: "#ffffff", fontWeight: 600 }}
                >
                  <MenuItem value="ALL">All payments</MenuItem>
                  <MenuItem value="ONLINE">Online / Prepaid</MenuItem>
                  <MenuItem value="COD">Cash on Delivery</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={1.25}>
              <Button
                variant="outlined"
                color="inherit"
                disabled={!hasActiveFilters}
                onClick={handleResetFilters}
                sx={{ minWidth: 96, borderRadius: 2, fontWeight: 700, textTransform: "none", borderColor: "#cbd5e1" }}
              >
                Reset all
              </Button>
              <Button
                variant="contained"
                onClick={() => setMobileFilterOpen(false)}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  bgcolor: "#004d40",
                  color: "#ffffff",
                  fontWeight: 800,
                  textTransform: "none",
                  py: 1.2,
                  boxShadow: "0 4px 14px rgba(0, 77, 64, 0.22)",
                  "&:hover": { bgcolor: "#00382e" },
                }}
              >
                Show {filteredOrders.length} order{filteredOrders.length === 1 ? "" : "s"}
              </Button>
            </Stack>
          </Drawer>
        </Box>
      )}

      {/* Orders DataGrid */}
      <CustomDataGrid
        rows={filteredOrders}
        columns={columns}
        styles={{
          height: "640px",
          bgcolor: "#ffffff",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.03)",
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
        pageSizes={[50, 100]}
        onRowClickHandle={(params) => setSelectedOrder({ ...params.row })}
        getRowClassName={(params) => {
          const status = (params?.row?.status || "").toLowerCase();
          return status.includes("cancelled") ? "cancelled-order-row" : "";
        }}
      />

      {/* View & Edit Order Modal */}
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openViewOrderModal}
        onClose={() => setOpenViewOrderModal(false)}
        closeAfterTransition
      >
        <Fade in={openViewOrderModal}>
          <Box sx={{ ...styles.boxStyle, p: 0, overflow: "hidden" }}>
            <ViewOrder
              detailedOrder={selectedOrder}
              onSetOrder={handleSetOrder}
              onDeleteOrder={handleDeleteOrder}
              hideModal={() => setOpenViewOrderModal(false)}
            />
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbarState}
        autoHideDuration={3500}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={message.type || "success"}
          sx={{ width: "100%", borderRadius: "10px", fontWeight: 600 }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllOrders;
